import axios from 'axios';
import { PublicClientApplication } from "@azure/msal-browser";
import { API_URL, API_TOKEN } from "./sys";
import { msalConfig } from "../config/msal-config";
import store from "../store";

// Create MSAL instance
const msalInstance = new PublicClientApplication(msalConfig);

// Create a promise to handle MSAL initialization
const msalInitializedPromise = msalInstance.initialize();

// Create an Axios instance
const axiosInstance = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
    "x-api-key": API_TOKEN,
  },
});

// Add a request interceptor to attach the auth token
axiosInstance.interceptors.request.use(
  async (config: any) => {
    try {
      // Ensure MSAL is initialized before proceeding
      await msalInitializedPromise;

      // Get all accounts
      const accounts = msalInstance.getAllAccounts();

      if (accounts.length > 0) {
        // Use the first account for token acquisition
        const account = accounts[0];
        // Define the scopes for your API - update this with your actual API scope
        const scopes = ["User.Read"];

        try {
          // Try to acquire token silently
          const tokenResponse = await msalInstance.acquireTokenSilent({
            account: account,
            scopes: scopes,
          });

          // Attach the token to the Authorization header
          config.headers.Authorization = `Bearer ${tokenResponse.accessToken}`;
        } catch (silentError) {
          console.warn("Silent token acquisition failed:", silentError);
        }
      } else {
        console.warn("No accounts found. User might need to sign in.");
      }

      // Attach userId header from store
      const state = store.getState();
      const userId = state?.formConfig?.user?.userId;
      if (userId) {
        config.headers['userId'] = userId;
      }
      const loginRole = state?.formConfig?.user?.role;
      if (loginRole) {
        config.headers['userRole'] = loginRole;
      }
    } catch (error) {
      console.error("Failed to acquire token:", error);
    }

    return config;
  },
  (error: any) => {
    return Promise.reject(error);
  },
);

// Add a response interceptor to handle token expiration
axiosInstance.interceptors.response.use(
  (response: any) => {
    return response;
  },
  async (error: any) => {
    const originalRequest = error.config;

    // Check if the error is due to an expired token (401 status)
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const accounts = msalInstance.getAllAccounts();
        if (accounts.length > 0) {
          const account = accounts[0];
          const scopes = ["User.Read"];
          const tokenResponse = await msalInstance.acquireTokenSilent({
            account: account,
            scopes: scopes,
            forceRefresh: true,
          });
          originalRequest.headers.Authorization = `Bearer ${tokenResponse.accessToken}`;
          return axiosInstance(originalRequest);
        }
      } catch (refreshError) {
        console.error("Token refresh failed:", refreshError);
      }
    }
    return Promise.reject(error);
  },
);

export const FetchRetailers = `${API_URL}/retailers`;
export const SendOTP = `${API_URL}/otp/send`;
export const VerifyOTPs = `${API_URL}/otp/verify`;

export const get = async (endpoint: string, params?: Record<string, string>): Promise<any> => {
  try {
    const response = await axiosInstance.get(endpoint, {
      params,
    });
    console.log("GET request successful:", response.data);
    return response.data;
  } catch (error: any) {
    if (axios.isAxiosError(error) && error.response) {
      const { status, data } = error.response;
      console.error(`HTTP error ${status}:`, data);
      throw new Error(`HTTP error ${status}: ${JSON.stringify(data)}`);
    } else {
      console.error("An unexpected error occurred:", error);
      throw new Error("An unexpected error occurred.");
    }
  }
};

export const getBlob = async (endpoint: string, params?: Record<string, string>): Promise<Blob> => {
  try {
    const response = await axiosInstance.get(endpoint, {
      params,
      responseType: 'blob',
    });
    return response.data;
  } catch (error: any) {
    if (axios.isAxiosError(error) && error.response) {
      const { status } = error.response;
      throw new Error(`HTTP error ${status}: Failed to download file`);
    } else {
      throw new Error("An unexpected error occurred.");
    }
  }
};

export const post = async (endpoint: string, data: any): Promise<any> => {
  try {
    const response = await axiosInstance.post(endpoint, data);
    console.log("POST request successful:", response.data);
    return response.data;
  } catch (error: any) {
    if (axios.isAxiosError(error) && error.response) {
      const { status, data } = error.response;
      if (data.message === "Failed to create retailer" && status === 500) {
        console.error("A survey is already under progress/completed for this user, please contact your Admin for more info");
        return;
      } else if (status === 409) {
        return { ...data, status: status };
      } else {
        console.error("Something went wrong:", data);
      }
      throw new Error(data.message || "Something went wrong!");
    } else {
      console.error("An unexpected error occurred:", error);
      throw new Error("An unexpected error occurred.");
    }
  }
};

export const put = async (endpoint: string, data: any): Promise<any> => {
  try {
    const response = await axiosInstance.put(endpoint, data);
    console.log("PUT request successful:", response.data);
    return response.data;
  } catch (error: any) {
    if (axios.isAxiosError(error) && error.response) {
      const { status, data } = error.response;
      console.error(`PUT request failed with status ${status}:`, data);
      throw new Error(data.message || "Something went wrong with the PUT request.");
    } else {
      console.error("An unexpected error occurred:", error);
      throw new Error("An unexpected error occurred.");
    }
  }
};

export const del = async (endpoint: string): Promise<any> => {
  try {
    const response = await axiosInstance.delete(endpoint);
    console.log("DELETE request successful:", response.data);
    return response.data;
  } catch (error: any) {
    if (axios.isAxiosError(error) && error.response) {
      const { status, data } = error.response;
      console.error(`DELETE request failed with status ${status}:`, data);
      throw new Error(data.message || "Something went wrong with the DELETE request.");
    } else {
      console.error("An unexpected error occurred:", error);
      throw new Error("An unexpected error occurred.");
    }
  }
};
